import createGraph, { Graph, Link, Node, NodeId } from 'ngraph.graph';
import path, { PathFinder } from 'ngraph.path';
import { Box3, Vector3Tuple } from 'three';
import { TRUCK_SPEED_METERS_PER_SECOND, WALKING_SPEED_METERS_PER_SECOND } from '../../config';
import { CartesianCoordinates } from '../../util/Events/schema';
import { groupBy } from '../../util/arrayUtils';
import { metresToFeet } from '../../util/numberUtils';
import {
  Navmesh,
  NavmeshArea,
  NavmeshEmissionZone,
  NavmeshLevel,
  NavmeshLink,
  NavmeshNode,
} from '../api/Navmesh';
import {
  horizontalQuaternion,
  quaternionFromPositions,
  releaseVector3,
  temporaryVector3,
} from '../util/vectorUtils';
import { MapForCache } from './MapForCache';
import {
  adjoiningIndexesOf,
  angleFromPoints,
  distancePointToSegment,
  euclideanDistance,
  euclideanDistanceAlongPath,
  joinWithReasonableAngles,
  pointBetween,
  pointIsBetweenTwoOthers,
  pointsAreEqual,
} from './navGraphGeometryUtils';

export type NavGraphNodeData = Omit<NavmeshNode, 'id'>;
export type NavGraphLinkData = Omit<NavmeshLink, 'a' | 'b'>;
export type NavGraphNode = Node<NavGraphNodeData>;
export type NavGraphLink = Link<NavGraphLinkData>;
export type NavPathfinder = PathFinder<NavGraphNodeData>;

export type NavGraphUnit = 'm' | 'ft';

const euclideanDistanceBetweenNodes = (fromNode: NavGraphNode, toNode: NavGraphNode) =>
  euclideanDistance(fromNode.data.pos, toNode.data.pos);

/**
 * Graph over navmesh nodes and links. Supports pathfinding (A*), closest-point queries,
 * and conversion to/from the Navmesh API. Unnavigable links are excluded from pathfinding.
 */
export class NavGraph {
  // primitive props
  clientId = '';
  projectId = '';
  protected _unit: NavGraphUnit = 'm';
  public get unit(): NavGraphUnit {
    return this._unit;
  }
  public set unit(value: NavGraphUnit) {
    this._unit = value;
  }

  createdAt = '';
  updatedAt: string | null = null;

  // data structures
  levels: Map<number, Omit<NavmeshLevel, 'id'>> = new Map();
  areas: Map<number, Omit<NavmeshArea, 'id'>> = new Map();
  emissionZones: Map<number, Omit<NavmeshEmissionZone, 'id'>> = new Map();

  protected graph: Graph<NavGraphNodeData, NavGraphLinkData> = createGraph<
    NavGraphNodeData,
    NavGraphLinkData
  >();
  private pathfinder: PathFinder<NavGraphNodeData>;

  // Record node and link ids in maps so that they can be iterated over in for...of loops,
  // which allow the use of the `break` keyword, rather than ngraph.graph's forEachLink
  // which cannot be broken early
  protected nodeIds: Map<number, boolean> = new Map();
  protected linkIds: Map<number, string> = new Map();

  get nodeCount() {
    return this.graph.getNodeCount();
  }
  get linkCount() {
    return this.graph.getLinkCount();
  }
  /** Next unused node id (max existing + 1). Returns 1 when graph has no nodes. */
  nextNodeId() {
    const max = this.nodeIds.size === 0 ? 0 : Math.max(...this.nodeIds.keys());
    return max + 1;
  }
  /** Next unused link id (max existing + 1). Returns 1 when graph has no links. */
  nextLinkId() {
    const max = this.linkIds.size === 0 ? 0 : Math.max(...this.linkIds.keys());
    return max + 1;
  }

  // Caching is used to speed up closest-point and relation ID queries
  private readonly SHORT_CIRCUIT_DISTANCE = 0.5;
  private closestPointCache: MapForCache<
    string,
    { linkId: number; x: number; y: number; z: number }
  > = MapForCache.newWithLimit(5000);
  private relationIdCache: MapForCache<string, number | null> = MapForCache.newWithLimit(1000);

  private convertMetresToNavUnit(n: number) {
    if (this.unit === 'ft') return metresToFeet(n);
    return n;
  }

  /** Builds a graph from navmesh if provided; or an empty graph if no navmesh. */
  constructor(navmesh?: Navmesh) {
    this.pathfinder = path.aStar(this.graph, {
      blocked: (_from, _to, link) => !!link.data.unnavigable,
      distance: euclideanDistanceBetweenNodes,
      heuristic: euclideanDistanceBetweenNodes,
    });

    if (!navmesh) return;

    navmesh.levels?.forEach(({ id, ...data }) => this.levels.set(id, data));
    navmesh.areas?.forEach(({ id, ...data }) => this.areas.set(id, data));
    navmesh.emissionZones?.forEach(({ id, ...data }) => this.emissionZones.set(id, data));
    navmesh.nodes?.forEach(({ id, pos }) => this.addNode(id, { pos }));
    navmesh.links?.forEach(({ a, b, ...data }) => this.addLink(a, b, data));

    this.unit = navmesh.unit === 'ft' ? 'ft' : 'm';
    this.clientId = navmesh.clientId;
    this.projectId = navmesh.projectId;
    this.createdAt = navmesh.createdAt;
    this.updatedAt = navmesh.updatedAt ?? null;
  }

  /** Serialises the graph to Navmesh shape. Options: linkIds to export only those links (and their nodes). */
  toJSON(options?: { linkIds?: number[] }): Navmesh {
    const nodes: NavmeshNode[] = [];
    const links: NavmeshLink[] = [];

    const wantedLinkIds = new Set<number>(options?.linkIds);
    const wantedNodeIds = new Set<NodeId>();

    this.graph.forEachLink(link => {
      if (options?.linkIds && !wantedLinkIds.has(link.data.id)) return;
      if (options?.linkIds) {
        wantedNodeIds.add(link.fromId);
        wantedNodeIds.add(link.toId);
      }
      links.push({ a: Number(link.fromId), b: Number(link.toId), ...link.data });
    });

    this.graph.forEachNode(node => {
      if (!node.links) return;
      if (options?.linkIds && !wantedNodeIds.has(node.id)) return;

      nodes.push({ id: Number(node.id), pos: node.data.pos });
    });
    const levels =
      this.levels.size === 0
        ? null
        : Array.from(this.levels.entries()).map(([id, data]) => ({ id, ...data }));
    const areas =
      this.areas.size === 0
        ? null
        : Array.from(this.areas.entries()).map(([id, data]) => ({ id, ...data }));
    const emissionZones =
      this.emissionZones.size === 0
        ? null
        : Array.from(this.emissionZones.entries()).map(([id, data]) => ({ id, ...data }));

    return {
      clientId: this.clientId,
      projectId: this.projectId,
      unit: this.unit,
      nodes,
      links,
      levels,
      areas,
      emissionZones,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
    };
  }

  /** Returns the node for id, or undefined. */
  getNode(id: NodeId) {
    return this.graph.getNode(id);
  }
  /** Returns the link for numeric id, or undefined. */
  getLink(id: number) {
    const ngraphId = this.linkIds.get(id);
    if (ngraphId === undefined) return;

    return this.graph.getLinkById(ngraphId);
  }

  /** Iterates nodes; return true-like value to stop. */
  forEachNode(callback: (node: NavGraphNode) => boolean | void | undefined) {
    this.graph.forEachNode(callback);
  }
  /** Iterates links; return true-like value to stop. */
  forEachLink(callback: (link: NavGraphLink) => boolean | void | undefined) {
    this.graph.forEachLink(callback);
  }

  addNode(id: number, data: NavGraphNodeData) {
    this.graph.addNode(id, data);
    this.nodeIds.set(id, true);
  }

  /** Adds a link between nodes specified by ID, with the given link data (must include id for the link). */
  addLink(a: number, b: number, data: NavGraphLinkData) {
    const link = this.graph.addLink(a, b, data);
    const ngraphId = link.id;
    this.linkIds.set(data.id, ngraphId);
  }

  /** Clears all nodes, links, levels, areas, emissionZones and metadata. */
  clear() {
    this.graph.clear();
    this.nodeIds.clear();
    this.linkIds.clear();
    this.clientId = '';
    this.projectId = '';
    this.unit = 'm';
    this.levels.clear();
    this.areas.clear();
    this.emissionZones.clear();
    this.closestPointCache.clear();
    this.relationIdCache.clear();
    this.createdAt = '';
    this.updatedAt = null;
  }

  /** Converts a distance in metres to the graph's unit (m or ft). */
  metresToNavUnit(amount: number) {
    if (this.unit === 'm') {
      return amount;
    }
    return metresToFeet(amount);
  }

  /**
   * Finds the closest point on any link to the given 3D point.
   * @returns The link and the closest point on it, or null if graph is empty or point has no closest link.
   */
  findClosestLinkPoint(
    fromPoint: [number, number, number],
  ): { link: NavGraphLink; point: [number, number, number] } | null {
    const key = `${fromPoint.map(p => Math.round(p)).join('/')}`;
    const cached = this.closestPointCache.get(key);
    if (cached) {
      const link = this.graph.getLinkById(cached.linkId.toString());
      if (link) {
        return { link, point: [cached.x, cached.y, cached.z] };
      }
    }

    let closestLink: NavGraphLink | null = null;
    let closestPoint: [number, number, number] | null = null;
    let minDistance = Infinity;

    for (const [linkId, ngraphLinkId] of this.linkIds) {
      const link = this.graph.getLinkById(ngraphLinkId);
      if (!link) {
        // Shouldn't ever happen, but check required for type safety
        console.warn(`Link id ${linkId.toString()} / ${ngraphLinkId} not found in graph!`);
        continue;
      }
      const a = this.graph.getNode(link.fromId)?.data.pos;
      const b = this.graph.getNode(link.toId)?.data.pos;
      if (!a || !b) continue;

      const { distance, point } = distancePointToSegment(fromPoint, a, b);

      if (distance < minDistance) {
        minDistance = distance;
        closestLink = link;
        closestPoint = point;
      }
      if (distance < this.SHORT_CIRCUIT_DISTANCE) break;
    }

    if (!closestPoint || !closestLink) return null;

    this.closestPointCache.set(key, {
      linkId: closestLink.data.id,
      x: closestPoint[0],
      y: closestPoint[1],
      z: closestPoint[2],
    });

    return { link: closestLink, point: closestPoint };
  }

  /** Returns the endpoint of the link (from or to node) nearest to the given point. */
  findClosestNodeForLinkPoint({
    link,
    point,
  }: {
    link: NavGraphLink;
    point: [number, number, number];
  }) {
    return [this.graph.getNode(link.fromId), this.graph.getNode(link.toId)]
      .filter((n): n is NavGraphNode => !!n)
      .sort((a, b) => euclideanDistance(point, a.data.pos) - euclideanDistance(point, b.data.pos))
      .at(0);
  }

  /**
   * Finds a path between two world positions. Endpoints are snapped to the nearest point on the graph.
   * Uses A* pathfinding algorithm; unnavigable links are excluded.
   * @returns Ordered path of [x,y,z] points (from → to), or undefined when there is no navigable path (including if from is omitted)
   */
  findPath(to: { x: number; y: number; z: number }, from?: { x: number; y: number; z: number }) {
    const closestTo = this.findClosestLinkPoint([to.x, to.y, to.z]);
    const closestFrom = from ? this.findClosestLinkPoint([from.x, from.y, from.z]) : null;

    if (!closestTo || !closestFrom) return;

    if (closestTo.link.id === closestFrom.link.id) {
      return closestFrom.link.data.unnavigable ? undefined : [closestFrom.point, closestTo.point];
    }

    const fromLinkIsNavigable = !closestFrom.link.data.unnavigable;
    const toLinkIsNavigable = !closestTo.link.data.unnavigable;

    return [
      this.pathfinder.find(closestFrom.link.fromId, closestTo.link.fromId).map(n => n.data.pos),
      this.pathfinder.find(closestFrom.link.toId, closestTo.link.fromId).map(n => n.data.pos),
      this.pathfinder.find(closestFrom.link.fromId, closestTo.link.toId).map(n => n.data.pos),
      this.pathfinder.find(closestFrom.link.toId, closestTo.link.toId).map(n => n.data.pos),
    ]
      .filter(p => p.length > 1)
      .map(p => {
        if (fromLinkIsNavigable) {
          p.push(closestFrom.point);
        }
        if (toLinkIsNavigable) {
          p.unshift(closestTo.point);
        }

        // Avoid doubling back at start or end
        if (p.length >= 3 && pointIsBetweenTwoOthers(p[0], p[1], p[2])) {
          p.splice(1, 1);
        }
        if (
          p.length >= 3 &&
          pointIsBetweenTwoOthers(p[p.length - 1], p[p.length - 2], p[p.length - 3])
        ) {
          p.splice(-2, 1);
        }

        // Avoid zero-length sections at start or end
        if (pointsAreEqual(p[0], p[1])) {
          p.splice(0, 1);
        }
        if (p.length > 1 && pointsAreEqual(p[p.length - 1], p[p.length - 2])) {
          p.splice(-1, 1);
        }
        return p;
      })
      .filter(p => p.length > 1)
      .map(p => ({
        path: p,
        distance: p.reduce(
          (acc, cur, i) => (i === 0 ? 0 : acc + euclideanDistance(cur, p[i - 1])),
          0,
        ),
      }))
      .sort(({ distance: a }, { distance: b }) => a - b)
      .at(0)
      ?.path.reverse();
  }

  /**
   * Finds the shortest path from `from` to the nearest of the `to` positions. Endpoints mapped to nearest point on graph; unnavigable links excluded.
   * @returns Path and length, or undefined if no from, no to targets, or no path. If already within 1 unit of a target, returns a straight segment.
   */
  findPathToNearest(
    to: { x: number; y: number; z: number }[],
    from?: { x: number; y: number; z: number },
  ): { path: Vector3Tuple[]; length: number } | undefined {
    if (!from || !to.length) {
      return;
    }
    const atDestination = to.find(
      pos => euclideanDistance([pos.x, pos.y, pos.z], [from.x, from.y, from.z]) < 1,
    );

    if (atDestination) {
      const start: Vector3Tuple = [from.x, from.y, from.z];
      const end: Vector3Tuple = [atDestination.x, atDestination.y, atDestination.z];
      return {
        path: [start, end],
        length: euclideanDistance(start, end),
      };
    }

    return to
      .map(pos => this.findPath(pos, from))
      .filter((path): path is Vector3Tuple[] => !!path && path.length > 1)
      .map(path => ({
        path,
        length: euclideanDistanceAlongPath(path),
      }))
      .sort((a, b) => a.length - b.length)
      .at(0);
  }

  /** Per-segment speeds along a path (walking in pedestrian areas, truck elsewhere), in graph units per second. */
  findPathSpeeds(path: [number, number, number][]) {
    return path.slice(1).map((pos, i) => {
      const midpoint = pointBetween(pos, path[i], 0.5);
      const closest = this.findClosestLinkPoint(midpoint);
      const areaId = closest?.link.data.area;
      if (areaId !== undefined && this.areas.get(areaId)?.category === 'pedestrian') {
        return this.convertMetresToNavUnit(WALKING_SPEED_METERS_PER_SECOND);
      }
      return this.convertMetresToNavUnit(TRUCK_SPEED_METERS_PER_SECOND);
    });
  }

  /** Orientation quaternion from position and optional velocity. When provided, velocity is used to determine
   * the direction of the link to use (from → to, or to → from).
   * Returns horizontal quaternion if off graph or no velocity.
   */
  quaternionFromPositionAndVelocity = (
    position: CartesianCoordinates | undefined,
    velocity: CartesianCoordinates | undefined,
  ) => {
    if (!position) return horizontalQuaternion;

    const nearest = this.findClosestLinkPoint([position.x, position.y, position.z]);
    if (!nearest) return horizontalQuaternion;

    const fromNode = this.graph.getNode(nearest.link.fromId);
    const toNode = this.graph.getNode(nearest.link.toId);
    if (!fromNode || !toNode) return horizontalQuaternion;

    const nodeA = temporaryVector3(
      fromNode.data.pos[0],
      fromNode.data.pos[1],
      fromNode.data.pos[2],
    );
    const nodeB = temporaryVector3(toNode.data.pos[0], toNode.data.pos[1], toNode.data.pos[2]);
    const velocityVec = temporaryVector3(velocity?.x ?? 0, velocity?.y ?? 0, velocity?.z ?? 0);
    const nodeAClone = temporaryVector3(nodeA.x, nodeA.y, nodeA.z);

    const angleOfLinkToVelocity = nodeAClone.sub(nodeB).angleTo(velocityVec);
    try {
      const [nodeToLookTowards, otherNode] =
        angleOfLinkToVelocity < Math.PI / 2 ? [nodeA, nodeB] : [nodeB, nodeA];
      const qt = quaternionFromPositions(otherNode, nodeToLookTowards);
      if (qt) {
        return qt;
      }
      return horizontalQuaternion;
    } finally {
      releaseVector3(nodeA, nodeAClone, nodeB, velocityVec);
    }
  };

  /** Links as a plain array of [fromPos, toPos, unnavigable, danger, id] tuples, filtered by selected levels/areas/zones. */
  toDenormalisedLinks({
    selectedLevels,
    selectedAreas,
    selectedZones,
  }: {
    selectedLevels: Map<number, true>;
    selectedAreas: Map<number, true>;
    selectedZones: Map<number, true>;
  }) {
    const denormalised: [
      from: Vector3Tuple,
      to: Vector3Tuple,
      unnavigable: boolean,
      danger: boolean,
      id: number,
    ][] = [];
    const noFilters =
      selectedLevels.size === 0 && selectedAreas.size === 0 && selectedZones.size === 0;

    this.graph.forEachLink(link => {
      const passesFilters =
        noFilters ||
        (link.data.level !== undefined && selectedLevels.has(link.data.level)) ||
        (link.data.area !== undefined && selectedAreas.has(link.data.area)) ||
        (link.data.emissionZone !== undefined && selectedZones.has(link.data.emissionZone));
      if (!passesFilters) return;

      const fromNode = this.graph.getNode(link.fromId);
      const toNode = this.graph.getNode(link.toId);
      if (!fromNode || !toNode) return;

      denormalised.push([
        fromNode.data.pos,
        toNode.data.pos,
        !!link.data.unnavigable,
        link.data.area !== undefined && this.areas.get(link.data.area)?.category === 'danger',
        link.data.id,
      ]);
    });

    return denormalised;
  }

  /** Polyline segments for display, filtered by selection; adjoining segments merged where angles allow. */
  toLines({
    selectedLevels,
    selectedAreas,
    selectedZones,
  }: {
    selectedLevels: Map<number, true>;
    selectedAreas: Map<number, true>;
    selectedZones: Map<number, true>;
  }): Vector3Tuple[][] {
    const lines: Vector3Tuple[][] = this.toDenormalisedLinks({
      selectedLevels,
      selectedAreas,
      selectedZones,
    }).map(([from, to]) => [from, to]);

    // Combine lines with the same endpoints (if the angle is reasonable)
    const seenLineIndex: Map<number, boolean> = new Map();
    let deduplicatedCount = 0;
    do {
      seenLineIndex.clear();
      deduplicatedCount = 0;

      const linesByEndpoint = groupBy(
        lines.flatMap((line, index) =>
          line.length < 2
            ? []
            : [
                { index, endpoint: line[0] },
                { index, endpoint: line[line.length - 1] },
              ],
        ),
        line => line.endpoint.join('/'),
      );

      lines.forEach((line, i) => {
        if (seenLineIndex.has(i) || line.length < 2) {
          return;
        }

        const adjoiningLines = linesByEndpoint[line[0].join('/')]
          .concat(linesByEndpoint[line[line.length - 1].join('/')])
          .flatMap(adjoiningLine => {
            if (adjoiningLine.index <= i || seenLineIndex.has(adjoiningLine.index)) return [];

            const [indexA, indexB] = adjoiningIndexesOf(line, lines[adjoiningLine.index]);
            if (indexA === undefined || indexB === undefined) return [];

            const part1 = indexA === -1 ? line.slice() : line.slice().reverse();
            const part2 =
              indexB === 0
                ? lines[adjoiningLine.index].slice(1)
                : lines[adjoiningLine.index].slice(0, -1).reverse();

            const joinAngle = angleFromPoints(
              part1[part1.length - 2],
              part1[part1.length - 1],
              part2[0],
            );
            if (joinAngle > (Math.PI * 5) / 6) return [];

            const deviationFromStraight = Math.min(joinAngle, Math.PI - joinAngle) % (Math.PI / 2);
            return { adjoiningLineIndex: adjoiningLine.index, part1, part2, deviationFromStraight };
          })
          .sort((a, b) => a.deviationFromStraight - b.deviationFromStraight)
          .at(0);

        if (!adjoiningLines) return;

        const { adjoiningLineIndex, part1, part2 } = adjoiningLines;

        const joinPoints = joinWithReasonableAngles(
          part1[part1.length - 2],
          part1[part1.length - 1],
          part2[0],
        );

        seenLineIndex.set(adjoiningLineIndex, true);

        lines[i] = [...part1.slice(0, -2), ...joinPoints, ...part2.slice(1)];
        lines[adjoiningLineIndex].length = 0;
        deduplicatedCount++;
      });
    } while (deduplicatedCount > 0);

    return lines.filter(l => l.length > 1 && euclideanDistanceAlongPath(l) > 0.001);
  }

  /** Level, area, or emissionZone id for a position (via nearest link). Returns undefined if > 8m from link. Cached. */
  relatedIdOfPoint = (
    position: { x: number; y: number; z: number } | undefined,
    relation: 'level' | 'area' | 'emissionZone',
  ) => {
    if (!position) return undefined;

    const key = `${this.clientId}/${this.projectId}/${this.updatedAt}/${relation}/${Math.round(
      position.x,
    )}/${Math.round(position.y)}/${Math.round(position.z)}`;
    const cached = this.relationIdCache.get(key);
    if (cached !== undefined) return cached ?? undefined;

    const from: Vector3Tuple = [position.x, position.y, position.z];

    const nearest = this.findClosestLinkPoint(from);
    const nearestRelation = nearest?.link.data[relation];
    if (!nearest || nearestRelation === undefined) {
      this.relationIdCache.set(key, null);
      return undefined;
    }

    const distanceToLink = euclideanDistance(from, nearest.point);

    if (distanceToLink > (this.unit === 'ft' ? metresToFeet(8) : 8)) {
      this.relationIdCache.set(key, null);
      return undefined;
    }

    this.relationIdCache.set(key, nearestRelation);

    return nearestRelation;
  };

  /**
   * Bounding box of link geometry.
   * @param linkIds Ids of links to include; if undefined, all links.
   * @param selection If set, only links passing level/area/emissionZone filters.
   * @returns Box3 or undefined if no links match.
   */
  getBounds(
    linkIds?: number[],
    selection?: {
      levels: Map<number, true>;
      areas: Map<number, true>;
      emissionZones: Map<number, true>;
    },
  ) {
    const noSelection =
      !selection ||
      (selection.levels.size === 0 &&
        selection.areas.size === 0 &&
        selection.emissionZones.size === 0);

    const relevantLinkIds = linkIds ?? this.linkIds.keys();
    const coords: Vector3Tuple[] = [];

    const nodeIds = new Set<NodeId>();
    for (const id of relevantLinkIds) {
      const ngraphLinkId = this.linkIds.get(id);
      const link = ngraphLinkId ? this.graph.getLinkById(ngraphLinkId) : undefined;
      if (!link) continue;

      const passesFilter =
        noSelection ||
        (link.data.level !== undefined && selection.levels.has(link.data.level)) ||
        (link.data.area !== undefined && selection.areas.has(link.data.area)) ||
        (link.data.emissionZone !== undefined &&
          selection.emissionZones.has(link.data.emissionZone));
      if (!passesFilter) continue;

      nodeIds.add(link.fromId);
      nodeIds.add(link.toId);
    }

    nodeIds.forEach(nId => {
      const node = this.graph.getNode(nId);
      if (node) {
        coords.push(node.data.pos);
      }
    });

    if (!coords.length) return;

    const coordsVec3 = coords.map(c => temporaryVector3(...c));
    const bounds = new Box3().setFromPoints(coordsVec3);
    releaseVector3(...coordsVec3);

    return bounds;
  }

  /** Human-readable label for the given level/area/zone selection (e.g. "Level 1, 2 areas"). */
  labelForSelectedView = (
    selectedLevels: Map<number, true>,
    selectedAreas: Map<number, true>,
    selectedZones: Map<number, true>,
  ) => {
    if (selectedLevels.size === 0 && selectedAreas.size === 0 && selectedZones.size === 0) {
      return undefined;
    }

    let levelsPart = selectedLevels.size === 0 ? undefined : `${selectedLevels.size} levels`;
    if (selectedLevels.size === 1) {
      const levelId = Array.from(selectedLevels.keys())[0];
      const levelName = this.levels.get(levelId)?.name;
      levelsPart = levelName ?? levelId.toString();
    }

    let areasPart = selectedAreas.size === 0 ? undefined : `${selectedAreas.size} areas`;
    if (selectedAreas.size === 1) {
      const areaId = Array.from(selectedAreas.keys())[0];
      const areaName = this.areas.get(areaId)?.name;
      areasPart = areaName ?? areaId.toString();
    }

    let zonesPart = selectedZones.size === 0 ? undefined : `${selectedZones.size} emission zones`;
    if (selectedZones.size === 1) {
      const zoneId = Array.from(selectedZones.keys())[0];
      const zoneName = this.emissionZones.get(zoneId)?.name;
      zonesPart = zoneName ?? zoneId.toString();
    }

    return [levelsPart, areasPart, zonesPart]
      .filter((x): x is string => x !== undefined)
      .join(', ');
  };
}
